import React, { useEffect, useState, useMemo } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase/config';
import { FaUser, FaCheckCircle, FaClock, FaUsers, FaExclamationTriangle } from 'react-icons/fa';

const MyReferrals = () => {
    const [allReferrals, setAllReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    // Load current user
    useEffect(() => {
        const storedUser = localStorage.getItem('htamsUser');
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                setCurrentUser(user);
            } catch (e) {
                console.error('Error parsing user data:', e);
                setCurrentUser(null);
            }
        }
    }, []);

    const currentUserId = useMemo(() => currentUser?.uid || '', [currentUser]);

    // Fetch all trainings and extract user's referrals
    useEffect(() => {
        if (!currentUserId) return;

        setLoading(true);
        setError(null);

        const trainingRef = ref(db, 'HTAMS/company/trainings');

        const unsubscribe = onValue(
            trainingRef,
            (snapshot) => {
                const data = snapshot.val();

                if (data && typeof data === 'object') {
                    const referrals = [];

                    // Loop through all trainings
                    Object.entries(data).forEach(([trainingId, training]) => {
                        if (training.participants) {
                            // Loop through participants
                            Object.entries(training.participants).forEach(([participantId, participant]) => {
                                // Check if this participant was referred by current user
                                if (participant.referredBy?.id === currentUserId) {
                                    referrals.push({
                                        ...participant,
                                        participantId,
                                        trainingId,
                                        trainingLocation: training.location || 'Location TBD',
                                        trainingVenue: training.venue || 'Venue TBD',
                                        trainingStartDate: training.startDate || 'Not specified',
                                        trainingEndDate: training.endDate || 'Not specified',
                                        trainingTime: training.time || 'Time TBD',
                                        trainingFees: training.fees || 0,
                                        status: participant.status || participant.confirmedByTrainer ? 'confirmed' : 'pending'
                                    });
                                }
                            });
                        }
                    });

                    setAllReferrals(referrals);
                } else {
                    setAllReferrals([]);
                }

                setLoading(false);
            },
            (error) => {
                console.error('Firebase error:', error);
                setError(error.message || 'Failed to load referrals');
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [currentUserId]);

    // Sort referrals by date (newest first)
    const sortedReferrals = useMemo(() => {
        return [...allReferrals].sort((a, b) => 
            new Date(b.registeredAt) - new Date(a.registeredAt)
        );
    }, [allReferrals]);

    // Statistics
    const stats = useMemo(() => {
        const total = allReferrals.length;
        const confirmed = allReferrals.filter(r => r.status === 'confirmed').length;
        const pending = allReferrals.filter(r => r.status !== 'confirmed').length;

        return { total, confirmed, pending };
    }, [allReferrals]);

    if (loading) {
        return (
            <div className="referrals-container">
                <div className="loading-container">
                    <div className="loading-content">
                        <div className="spinner"></div>
                        <h3>Loading Your Referrals</h3>
                    </div>
                </div>
                <style jsx>{`
                    .referrals-container {
                        padding: 1rem;
                        max-width: 1200px;
                        margin: 0 auto;
                    }
                    .loading-container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        min-height: 200px;
                    }
                    .loading-content {
                        text-align: center;
                    }
                    .spinner {
                        border: 4px solid rgba(0, 0, 0, 0.1);
                        width: 36px;
                        height: 36px;
                        border-radius: 50%;
                        border-left-color: #4f46e5;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 1rem;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    h3 {
                        color: #374151;
                        margin: 0.5rem 0;
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div className="referrals-container">
                <div className="error-container">
                    <FaExclamationTriangle className="error-icon" />
                    <h3>Unable to Load Referrals</h3>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()} className="retry-button">
                        Try Again
                    </button>
                    <style jsx>{`
                        .error-container {
                            text-align: center;
                            padding: 2rem;
                            background: #fef2f2;
                            border-radius: 0.5rem;
                            color: #b91c1c;
                        }
                        .error-icon {
                            font-size: 2rem;
                            margin-bottom: 0.75rem;
                        }
                        .retry-button {
                            margin-top: 1rem;
                            padding: 0.5rem 1rem;
                            background: #ef4444;
                            color: #fff;
                            border: none;
                            border-radius: 0.375rem;
                            cursor: pointer;
                        }
                    `}</style>
                </div>
            </div>
        );
    }

    return (
        <div className="referrals-container">
            {/* Header */}
            <div className="page-header">
                    <h1 className="page-title">
                        <FaUsers className="title-icon" />
                        My Referrals
                    </h1>
                    <p className="page-subtitle">
                        All candidates you've referred across all training programs
                    </p>
            </div>

            {/* Stats */}
            <div className="stats-container">
                <div className="stat-card total">
                    <div className="stat-icon">
                        <FaUsers />
                    </div>
                    <div className="stat-content">
                        <h3>Total Referrals</h3>
                        <p className="stat-number">{stats.total}</p>
                    </div>
                </div>
                
                <div className="stat-card confirmed">
                    <div className="stat-icon">
                        <FaCheckCircle />
                    </div>
                    <div className="stat-content">
                        <h3>Confirmed</h3>
                        <p className="stat-number">{stats.confirmed}</p>
                    </div>
                </div>
                
                <div className="stat-card pending">
                    <div className="stat-icon">
                        <FaClock />
                    </div>
                    <div className="stat-content">
                        <h3>Pending</h3>
                        <p className="stat-number">{stats.pending}</p>
                    </div>
                </div>
            </div>

            {/* Referrals List */}
            {error ? (
                <div className="error-message">
                    <p>{error}</p>
                </div>
            ) : sortedReferrals.length === 0 ? (
                <div className="no-results">
                    <p>No referrals found.</p>
                </div>
            ) : (
                <div className="referrals-list">
                    {sortedReferrals.map((referral, index) => (
                        <div 
                            key={`${referral.participantId}-${index}`} 
                            className={`referral-card ${referral.status}`}
                        >
                            <div className="referral-header">
                                <h3 className="referral-name">
                                    <FaUser /> {referral.name || 'No Name'}
                                </h3>
                                <span className={`status-badge ${referral.status}`}>
                                    {referral.status === 'confirmed' ? (
                                        <><FaCheckCircle /> Confirmed</>
                                    ) : (
                                        <><FaClock /> Pending</>
                                    )}
                                </span>
                            </div>
                            
                            <div className="referral-details">
                                <p className="referral-email">{referral.email || 'No email'}</p>
                                <p className="referral-phone">{referral.phone || 'No phone'}</p>
                                <p className="referral-location">{referral.trainingLocation || 'No location'}</p>
                                <p className="referral-date">
                                    {new Date(referral.registeredAt).toLocaleDateString('en-IN')}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            <style jsx>{`
                .referrals-container {
                    padding: 1rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                .page-title {
                    color: #1f2937;
                    margin-bottom: 1.5rem;
                    font-size: 1.5rem;
                    font-weight: 600;
                }
                
                /* Stats Cards */
                .stats-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }
                
                .stat-card {
                    background: white;
                    border-radius: 0.5rem;
                    padding: 1.25rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    border-left: 4px solid #4f46e5;
                }
                
                .stat-card.confirmed {
                    border-left-color: #10b981;
                }
                
                .stat-card.pending {
                    border-left-color: #f59e0b;
                }
                
                .stat-icon {
                    width: 2.5rem;
                    height: 2.5rem;
                    border-radius: 50%;
                    background: #eef2ff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #4f46e5;
                    font-size: 1.25rem;
                }
                
                .stat-card.confirmed .stat-icon {
                    background: #ecfdf5;
                    color: #10b981;
                }
                
                .stat-card.pending .stat-icon {
                    background: #fffbeb;
                    color: #f59e0b;
                }
                
                .stat-content h3 {
                    margin: 0;
                    font-size: 0.875rem;
                    color: #6b7280;
                    font-weight: 500;
                }
                
                .stat-number {
                    margin: 0.25rem 0 0;
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #1f2937;
                }
                
                /* Referral Cards */
                .referrals-list {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 1rem;
                }
                
                .referral-card {
                    background: white;
                    border-radius: 0.5rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                
                .referral-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                }
                
                .referral-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-bottom: 1px solid #e5e7eb;
                }
                
                .referral-name {
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 500;
                    color: #1f2937;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .status-badge {
                    font-size: 0.75rem;
                    font-weight: 500;
                    padding: 0.25rem 0.5rem;
                    border-radius: 9999px;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                
                .status-badge.confirmed {
                    background: #ecfdf5;
                    color: #10b981;
                }
                
                .status-badge.pending {
                    background: #fffbeb;
                    color: #d97706;
                }
                
                .referral-details {
                    padding: 1rem;
                    font-size: 0.875rem;
                    color: #4b5563;
                }
                
                .referral-details p {
                    margin: 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .referral-email,
                .referral-phone,
                .referral-location,
                .referral-date {
                    color: #6b7280;
                    font-size: 0.875rem;
                }
                
                .referral-date {
                    color: #9ca3af;
                    font-size: 0.75rem;
                    margin-top: 0.5rem;
                    padding-top: 0.5rem;
                    border-top: 1px dashed #e5e7eb;
                }
                
                /* Error and No Results */
                .error-message,
                .no-results {
                    text-align: center;
                    padding: 2rem;
                    background: #fef2f2;
                    border-radius: 0.5rem;
                    color: #b91c1c;
                    margin: 1rem 0;
                }
                
                .no-results {
                    background: #f3f4f6;
                    color: #6b7280;
                }
                
                @media (max-width: 640px) {
                    .stats-container {
                        grid-template-columns: 1fr;
                    }
                    
                    .referrals-list {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default MyReferrals;
